import {
  Component,
  ChangeDetectionStrategy,
  HostListener,
  signal,
  inject,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';

import { debounceTime, Subject, takeUntil } from 'rxjs';

import { SECTIONS } from '../../core/data/sections';
import { SearchBarComponent } from '../search-bar/search-bar.component';

export interface MenuItem {
  label: string;
  link?: string;
  variant?: string;
  children?: MenuItem[];
}

@Component({
  selector: 'app-header',
  imports: [NgClass, RouterLink, RouterLinkActive, SearchBarComponent],
  templateUrl: './header.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header.scss',
})
export class Header {
  private router = inject(Router);

  protected readonly sections = SECTIONS;
  protected readonly socialLinks = [
    {
      icon: 'fab fa-youtube',
      url: 'https://www.youtube.com/channel/UCsD71qFZPW42WbdW-aVg8vA',
      label: 'YouTube',
    },
    {
      icon: 'fab fa-tiktok',
      url: 'https://www.tiktok.com/@arteyculturacrbsp',
      label: 'TikTok',
    },
    {
      icon: 'fab fa-x-twitter',
      url: 'https://x.com/arteculturacrsp',
      label: 'Twitter',
    },
    {
      icon: 'fab fa-instagram',
      url: 'https://www.instagram.com/arteyculturacrbsp',
      label: 'Instagram',
    },
    {
      icon: 'fab fa-facebook',
      url: 'https://www.facebook.com/arteyculturags',
      label: 'Facebook',
    },
  ];
  readonly items: MenuItem[] = [
    {
      label: 'Inicio',
      link: '/',
    },
    {
      label: 'Archivo',
      link: '/archivo',
      variant: 'text-dark',
    },
    {
      label: 'Categorías',
      variant: 'text-dark',
      children: SECTIONS.map((section) => {
        return {
          label: section.name,
          link: `/seccion/${section.slug}`,
        };
      }),
    },
    {
      label: 'Video',
      variant: 'text-dark',
      link: '/videos',
    },
  ];
  readonly secondaryItems: MenuItem[] = [
    {
      label: 'Nosotros',
      link: '/acerca-de-nosotros',
    },
    {
      label: 'Contacto',
      link: '/contacto',
    },
    {
      label: 'Newsletter',
      link: '/newsletter',
    },
  ];

  protected searchOpen = false;
  private destroy$ = new Subject<void>();
  private resize$ = new Subject<void>();
  private navbar: HTMLElement | null = null;

  bannerHeight = signal<number>(58);

  @HostListener('window:resize')
  onResize() {
    this.resize$.next();
  }

  ngAfterViewInit(): void {
    this.navbar = document.getElementById('navbar');

    this.resize$
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => this.handleBannerHeight());

    this.handleBannerHeight();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleBannerHeight() {
    if (!this.navbar) return;

    const doc = document.documentElement;
    const height = this.navbar.clientHeight;

    this.bannerHeight.set(height);

    if (height > 0) {
      doc.style.setProperty('--header-height', `${height}px`);
    }
  }

  toggleSearch(): void {
    this.searchOpen = !this.searchOpen;
  }

  close() {
    this.searchOpen = false;
  }

  handleSearch(query: string): void {
    this.searchOpen = false;

    this.router.navigate(['/buscar'], { queryParams: { s: query } });
  }
}
